import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReactModal from "react-modal";
import { useTheme } from "styled-components";

import { requestSummary_Aggregated } from "../../store/requests/thunks/requestSummary_Aggregated";
import { requestEmailUterum } from "../../store/requests/thunks/requestEmailUterum";
import { requestAddToCart } from "../../store/requests/thunks/requestAddToCart";
import { setRequestsAsIdle } from "../../store/requests/requestsSlice";

import { getConservatoryAlias } from "../../helpers/conservatories";
import { alert } from "../../helpers/alert";
import { getSeasonName } from "../../helpers/seasonName";
import { formatPrice } from "../../helpers/formatPrice";

import { BFC_SPA_Uterum } from "../../constants/constants";
import { emptyStoredState } from "../../store/storedState";

import SkeletonView from "../Skeleton/SkeletonView";

import { AddToCart } from "../../components/AddToCart/AddToCart";
import { PrintFrontPage } from "../../components/PrintFrontPage/PrintFrontPage";
import { CartSummary } from "../../components/CartSummary/CartSummary";
import { ArticleList } from "../../components/ArticleList/ArticleList";
import { MeasureSketches } from "../../components/MeasureSketches/MeasureSketches";
import { modalStyle } from "../../components/Modal/ModalStyle";
import { PdfIcon } from "../../assets/PdfIcon";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import {
  DocumentTitle,
  DocumentTitleText,
  EmailFormatError,
  MailContainer,
  PrintContainer,
  OptInText,
  ModalFooter,
  ArticlePriceModal,
  ModalHeader,
  OrdinaryPrice,
  DiscountedPrice,
  TotalPriceLabel,
  PrintButton,
  SendMailButton,
  OrderHeadline,
  ConservatoryContainer,
  ConservatoryInformation,
  ConservatoryData,
} from "./SummaryViewStyle";

const SummaryView = () => {
  const dispatch = useDispatch();

  const init = useSelector((state) => state.init);
  const Uterum = useSelector((state) => state.Uterum);
  const Result = useSelector((state) => state.Result);
  const requests = useSelector((state) => state.requests);

  const { currency, baseRoute, alias } = init;

  // TODO const Uterum osv.
  const frameType = useSelector((state) => state.Uterum.FrameType);
  const articles = useSelector((state) => state.Result.Articles);
  const summary = useSelector((state) => state.Result.Summary);
  const uniqueDocuments = useSelector((state) => state.Result.UniqueDocuments);

  const [mailOptIn, setMailOptIn] = useState(false);
  const [email, setEmail] = useState("");
  const [emailFormatError, setEmailFormatError] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const theme = useTheme();
  const navigate = useNavigate();
  let params = new URL(document.location).searchParams;

  const key = params.get("Key") || params.get("key") || params.get("KEY");

  // Init when view did mount --- Cleanup when view unmounts
  useEffect(() => {
    window.scrollTo(0, 0);

    if (window.location.pathname.includes("/gotosummary")) {
      if (!key) {
        console.warn(
          "SummaryView: URL param key is missing or incorrect for path /gotosummary"
        );
        navigate(`../${baseRoute}/step1`, { replace: true });
      }
    }
    
    dispatch(
      key
        ? requestSummary_Aggregated({
            urlParamKey: key,
          })
        : requestSummary_Aggregated()
    );

    return () => {
      // TODO 3062: Maybe cleanup email and add to cart ("idle") here?
      dispatch(setRequestsAsIdle(["requestSummary_Aggregated"]));
    };
  }, [dispatch]);

  // Handle rejected
  // TODO 3062: Add error handling for email and add to cart
  useEffect(() => {
    if (requests.status.requestSummary_Aggregated === "rejected") {
      alert({
        title: alias.bfcGenericErrorMessage,
        text: alias.bfcContactUs,
        icon: "error",
        confirmButtonColor: theme.primary,
        onConfirm: () => {
          key
            ? navigate(`../${baseRoute}/step1`, { replace: true })
            : navigate(`../${baseRoute}/step4`, { replace: true });
        },
      });
    }
  }, [requests.status.requestSummary_Aggregated]);

  const handleAddToCart = () => {
    // TODO 3062 Add some error handling
    dispatch(requestAddToCart()).then(() => {
      openModal();
    });
  };

  function openModal() {
    setModalIsOpen(true);
  }
  function closeModal() {
    setModalIsOpen(false);
    // The reload is to trigger a refresh so that the cart quantity will update
    window.location.reload();
  }

  const validateEmail = (email) => {
    const regex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return regex.test(email);
  };

  const handleEmailSubmit = () => {
    const emailValidated = validateEmail(email);

    if (emailValidated) {
      // TODO 3062 Add some error handling
      dispatch(
        requestEmailUterum({
          email: email,
          newsletter: mailOptIn,
        })
      ).then(() => {
        setEmailFormatError(null);
        setEmail("");
        alert({
          title: alias.bfcEmailSent,
          text: alias.bfcConfigSavedMessage,
          icon: "success",
          confirmButtonColor: theme.primary,
        });
      });
    } else {
      setEmailFormatError(alias.bfcIncorrectEmail);
    }
  };

  const handleEmailChange = (email) => {
    setEmail(email);
    setEmailFormatError(null);
  };

  function updateOptInValue() {
    setMailOptIn(!mailOptIn);
  }

  const renderMode = (() => {
    switch (requests.status.requestSummary_Aggregated) {
      case "idle":
      case "pending":
        return "loader";
      case "fulfilled":
        return "default";
      case "rejected":
        return "none";
      default:
        return null;
    }
  })();

  if (renderMode === "loader") {
    return <SkeletonView forView="SummaryView" />;
  }

  if (renderMode === "default") {
    return (
      <>
        <PrintFrontPage />
        <ConservatoryContainer>
          <ConservatoryInformation className="hide-on-print">
            <Typography variant="h3">{getConservatoryAlias(frameType, alias)}</Typography>
            <h4>
              <hr />
              <ConservatoryData>
                {Result.SquareMeterStomme} m<sup>2</sup>
              </ConservatoryData>
              <ConservatoryData>
                {Uterum.FrameWidth} x {Uterum.FrameDepth} mm
              </ConservatoryData>
              <ConservatoryData>
                {getSeasonName(alias, Uterum.Season)}
              </ConservatoryData>
              <hr />
            </h4>
          </ConservatoryInformation>
        </ConservatoryContainer>
        <OrderHeadline>{alias.bfcYourOrder}</OrderHeadline>
        <CartSummary>
          <>
            <ArticleList articles={articles} />
            
            <Box sx={{display: {xs: 'none', sm: 'block'}, displayPrint: 'none'}}>
              <Typography variant="body2">
                {alias.bfcArticlesInCartInfo}
              </Typography>
            </Box>
          </>
        </CartSummary>
        
        <Grid container spacing={2}
          sx={{
            borderTop: '1px solid rgb(179, 179, 179)',
            borderBottom: '1px solid rgb(179, 179, 179)',
            width: { xs: '100vw', md: '100%' },
            marginLeft: {xs: '-15px', md: 0},
          }}
        >
          <Grid item xs={12} md={4} p={2}>
            <MailContainer className="hide-on-print">
              <Typography variant="h4">{alias.bfcSaveConfiguration}</Typography>
              <input
                type="text"
                className="email-input"
                name={email}
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder={alias.bfcWriteEmail}
              />
              <OptInText>
                <input
                  type="checkbox"
                  id="opt-in"
                  name="opt-in"
                  onChange={() => updateOptInValue()}
                />
                <label htmlFor="opt-in">{alias.bfcMarketingLabel}</label>
              </OptInText>
              <SendMailButton
                data-id="SendMailButton"
                onClick={() => handleEmailSubmit()}
              >
                {alias.bfcSaveConfigurationText}
              </SendMailButton>
              {emailFormatError && (
                <EmailFormatError>{emailFormatError}</EmailFormatError>
              )}
            </MailContainer>
          </Grid>

          <Grid item xs={12} md={4} p={2}>
            <PrintContainer className="hide-on-print">
              <Typography variant="h4">{alias.bfcPriceInfo}</Typography>
              <Typography variant="body1">{alias.bfcDisclaimer}</Typography>
              <PrintButton onClick={() => window.print()} data-id="PrintButton">
                {alias.bfcPrint}
              </PrintButton>
            </PrintContainer>
          </Grid>

          <Grid item xs={12} md={4} sx={{backgroundColor: '#F1F1F1'}} p={2}>
            <AddToCart
              summary={summary}
              alias={alias}
              onAddToCart={handleAddToCart}
            />
            <ReactModal
              isOpen={modalIsOpen}
              data={{ background: "green" }}
              onRequestClose={closeModal}
              appElement={document.getElementById("bfc-single-page-app")}
              style={modalStyle}
            >
              <CartSummary>
                <ModalHeader>{alias.bfcAddedToCart}</ModalHeader>

                <ArticleList articles={articles} />

                <ArticlePriceModal>
                  <TotalPriceLabel>{alias.bfcTotalPrice}</TotalPriceLabel>
                  {/* Check if there is a discounted price, and render prices and layout depending on that */}
                  {summary.Price === summary.OrdinaryPrice ? (
                    <span>
                      {formatPrice(summary.OrdinaryPrice, currency)}
                    </span>
                  ) : (
                    <div>
                      <DiscountedPrice>
                        {formatPrice(summary.Price, currency)}
                      </DiscountedPrice>
                      <OrdinaryPrice>
                        {formatPrice(summary.OrdinaryPrice, currency)}
                      </OrdinaryPrice>
                    </div>
                  )}
                </ArticlePriceModal>
              </CartSummary>
              <ModalFooter>
                <button onClick={() => closeModal()}>{alias.bfcClose}</button>
                <form
                  onSubmit={() => emptyStoredState(BFC_SPA_Uterum)}
                  action={`${requests.apiConfigs.scensumBaseUrl}/checkout`}
                >
                  <button>{alias.bfcGotoCheckout}</button>
                </form>
              </ModalFooter>
            </ReactModal>
          </Grid>
        </Grid>

        <Box mt={4} sx={{pageBreakBefore: 'always'}}>
          <Typography variant="h3">{alias.bfcDimensions}</Typography>
          <MeasureSketches />
        </Box>

        <Box mt={4} className="hide-on-print">
          <Typography variant="h3">{alias.bfcDocuments}</Typography>
          <Grid container spacing={2} mt={{xs:'8px',sm:1}}>
            <Grid item>
              <h4>{alias.bfcInstructions}</h4>
              {uniqueDocuments.map((document) => (
                <React.Fragment key={document.Url}>
                  {document.Type === "AssemblyInstructions" && (
                    <a
                      id={`${document.Type}_${document.Title}`}
                      href={`${document.Url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <DocumentTitle>
                        <PdfIcon />
                        <DocumentTitleText>{document.Title}</DocumentTitleText>
                      </DocumentTitle>
                    </a>
                  )}
                </React.Fragment>
              ))}
            </Grid>
          
            <Grid item>
              <h4>{alias.bfcOtherDocuments}</h4>
              {uniqueDocuments.map((document) => (
                <React.Fragment key={document.Url}>
                  {document.Type === "GeneralDocument" && (
                    <a
                      href={`${document.Url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <DocumentTitle>
                        <PdfIcon />
                        <DocumentTitleText>{document.Title}</DocumentTitleText>
                      </DocumentTitle>
                    </a>
                  )}
                </React.Fragment>
              ))}
            </Grid>
          </Grid>
        </Box>
      </>
    );
  }

  /*
  If status for request(s) is 'rejected' --> renderMode is "none"
  --> error handling for UI is done in useEffect
  */
  return null;
};

export default SummaryView;
